<script setup lang="ts">
import { useAuthStore } from './stores/auth'
import { useSubscriptionStore } from './stores/subscription'
import { useLanguageStore } from './stores/language'
import { useRoute } from 'vue-router'
import { computed, onMounted, watch, ref } from 'vue'
import './assets/main.css'
import AppHeader from './components/AppHeader.vue'
import AppSidebar from './components/AppSidebar.vue'
import SubscriptionExpirationModal from './components/SubscriptionExpirationModal.vue'

// Element Plus Locales
import tr from 'element-plus/es/locale/lang/tr'
import en from 'element-plus/es/locale/lang/en'
import de from 'element-plus/es/locale/lang/de'
import es from 'element-plus/es/locale/lang/es'
import fr from 'element-plus/es/locale/lang/fr'
import it from 'element-plus/es/locale/lang/it'
import nl from 'element-plus/es/locale/lang/nl'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import ru from 'element-plus/es/locale/lang/ru'
import ar from 'element-plus/es/locale/lang/ar'

const route = useRoute()
const auth = useAuthStore()
const subscriptionStore = useSubscriptionStore()
const languageStore = useLanguageStore()

// Kiosk tam ekran modunda veya kiosk rolündeki kullanıcıda header/sidebar gizle
const hideLayoutForKiosk = computed(() => !!route.meta.hideLayout || !!auth.user?.is_kiosk_user)

// Element Plus locale mapping
const elementPlusLocales: Record<string, any> = {
  tr,
  en,
  de,
  es,
  fr,
  it,
  nl,
  zh: zhCn,
  ru,
  ar
}

// Current Element Plus locale based on language store
const currentLocale = computed(() => {
  return elementPlusLocales[languageStore.currentLanguage] || tr
})

// Sidebar açık/kapalı durumu
const isSidebarCollapsed = ref(false)

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
}

// Sayfayı yenileme durumunda localStorage'dan abonelik bilgisini yükle
onMounted(() => {
  if (auth.isAuthenticated()) {
    const storedSubscription = localStorage.getItem('subscription')
    if (storedSubscription) {
      try {
        const subscription = JSON.parse(storedSubscription)
        const endDate = subscription.end_date ? new Date(subscription.end_date) : null
        const today = new Date()
        const endDateOk = endDate && endDate > today
        const hasGrace = subscription.has_unpaid_order_with_future_date === true
        const isActive = (subscription.status === 'active' && endDateOk) || hasGrace
        
        // Store'a kaydet
        subscriptionStore.setSubscriptionStatus(isActive)
        subscriptionStore.setSubscriptionData(subscription)
      } catch (e) {
        console.error('App.vue: Abonelik verisi parse edilemedi:', e)
      }
    }
  }
})

// Abonelik verisi değiştiğinde expiration modal'ı kontrol et
watch(() => subscriptionStore.subscriptionData, (newData) => {
  // Sadece veri gerçekten değiştiğinde kontrol et
  if (newData && newData.id) {
    subscriptionStore.checkAndSetExpirationModal()
  }
}, { deep: true })

// Sidebar'ın gösterilip gösterilmeyeceğini belirleyen computed property
const showSidebar = computed(() => {
  // Her zaman göster (modal içinde subscription sayfası gösterilecek)
  return true
})

// Mevcut dil bayrağı
const currentLanguageFlag = computed(() => {
  const lang = languageStore.languages.find(l => l.code === languageStore.currentLanguage)
  return lang?.flag || '🌐'
})
</script>

<template>
  <el-config-provider 
    :locale="currentLocale" 
    size="small"
    :z-index="2000"
    :message="{ max: 3 }"
    :button="{ autoInsertSpace: false }"
  >
    <div class="app">
      <!-- Dil değişimi overlay -->
      <Transition name="lang-fade">
        <div v-if="languageStore.isChangingLanguage" class="language-overlay">
          <div class="language-spinner">
            <div class="spinner-ring"></div>
            <span class="spinner-text">{{ currentLanguageFlag }}</span>
          </div>
        </div>
      </Transition>

      <template v-if="auth.isAuthenticated()">
        <template v-if="!hideLayoutForKiosk">
          <AppHeader @toggle-sidebar="toggleSidebar" :sidebar-collapsed="isSidebarCollapsed" />
          <AppSidebar v-if="showSidebar" :collapsed="isSidebarCollapsed" />
          <div class="main-wrapper" :class="{ 'no-sidebar': !showSidebar, 'sidebar-collapsed': isSidebarCollapsed }">
            <main class="main-content">
              <router-view></router-view>
            </main>
          </div>
        </template>
        <router-view v-else></router-view>
        
        <!-- Subscription Expiration Modal -->
        <SubscriptionExpirationModal 
          :show="subscriptionStore.showExpirationModal"
        />
      </template>
      <router-view v-else></router-view>
    </div>
  </el-config-provider>
</template>

<style>
.app {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Dil değişimi overlay */
.language-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.language-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner-ring {
  width: 60px;
  height: 60px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-text {
  font-size: 32px;
  animation: pulse 0.6s ease-in-out infinite alternate;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  from { transform: scale(1); opacity: 0.8; }
  to { transform: scale(1.2); opacity: 1; }
}

/* Overlay geçiş animasyonu */
.lang-fade-enter-active,
.lang-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lang-fade-enter-from,
.lang-fade-leave-to {
  opacity: 0;
}

.main-wrapper {
  margin-left: 240px;
  margin-top: 45px;
  min-height: calc(100vh - 45px);
  transition: margin-left 0.3s ease;
}

.main-wrapper.sidebar-collapsed {
  margin-left: 64px;
}

.main-wrapper.no-sidebar {
  margin-left: 0;
}

.main-content {
  flex: 1;
  padding: 10px;
}

@media (max-width: 768px) {
  .main-wrapper {
    margin-left: 60px;
  }
  
  .main-wrapper.no-sidebar {
    margin-left: 0;
  }

  .main-content {
    padding: 10px;
  }
}

</style>
